/**
 * Firebase Authentication Setup - Web Platform
 *
 * Initializes Firebase app and Auth for FleetHub B2B customer login on web.
 * Uses browserLocalPersistence to store auth state in localStorage.
 *
 * This file is automatically used by Metro bundler when running on web platform.
 * For native platforms (iOS/Android), see auth.ts
 *
 * Reference: https://firebase.google.com/docs/auth/web/auth-state-persistence
 * Expo Setup Guide: https://github.com/expo/fyi/blob/main/firebase-js-auth-setup.md
 *
 * @module services/firebase/auth
 * @platform web
 */

import { initializeApp, getApps, FirebaseApp } from "firebase/app"
import { Auth, initializeAuth, browserLocalPersistence } from "firebase/auth"

import Config from "@/config"

// Firebase configuration from environment variables
// These values are safe to expose in frontend code - they only identify your Firebase project
// Security is enforced by Firebase Authentication and Firestore Security Rules on the backend
const firebaseConfig = {
  apiKey: Config.FIREBASE_API_KEY,
  authDomain: Config.FIREBASE_AUTH_DOMAIN,
  projectId: Config.FIREBASE_PROJECT_ID,
  storageBucket: Config.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: Config.FIREBASE_MESSAGING_SENDER_ID,
  appId: Config.FIREBASE_APP_ID,
}

// Initialize Firebase app (singleton pattern)
let firebaseApp: FirebaseApp
if (!getApps().length) {
  firebaseApp = initializeApp(firebaseConfig)
} else {
  firebaseApp = getApps()[0]
}

// Initialize Firebase Auth with web-specific persistence
// Uses browserLocalPersistence which stores auth tokens in localStorage
// This ensures auth state persists across page reloads and browser sessions
const auth: Auth = initializeAuth(firebaseApp, {
  persistence: browserLocalPersistence,
})

/**
 * Resolves once Firebase Auth has rehydrated its persisted state from localStorage.
 *
 * Firebase JS SDK v10+ exposes `auth.authStateReady()` which resolves on the first
 * auth-state emission. Awaiting this before firing callables prevents the cold-load
 * race where browserLocalPersistence rehydration is async (~50-500 ms) and
 * auth.currentUser is null even though the user is signed in.
 */
export function waitForAuthReady(): Promise<void> {
  return auth.authStateReady()
}

export { auth }
export default firebaseApp
